import { el } from '../dom.js';
import type { I18n } from '../i18n.js';
import type { ScenarioView, ResponseAck, SkipReason, Reflection, EmphasisTag } from '../session.js';
import { api, SKIP_REASONS, MOTIVATION_TAGS } from '../session.js';

/**
 * §7.1 v2.3 — ResumePrompt shows a non-blocking dismissible toast at the
 * post-twist scenario (position 15 in 27-slot plans). Two buttons: continue
 * inline or save-and-exit. Auto-dismisses after 8 seconds of inactivity so it
 * never blocks flow.
 */
export interface ResumePromptProps {
  i18n: I18n;
  onContinue: () => void;
  onSaveAndExit: () => void;
  autoDismissMs?: number;
}

export function ResumePrompt({
  i18n,
  onContinue,
  onSaveAndExit,
  autoDismissMs = 8000,
}: ResumePromptProps): HTMLElement {
  const root = el('aside', {
    class: 'resume-prompt',
    role: 'dialog',
    'aria-label': i18n.t('session.resume_prompt.title'),
  });

  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    root.classList.add('resume-prompt-dismissed');
    root.remove();
  };

  const continueBtn = el('button', { class: 'secondary', type: 'button' },
    i18n.t('session.resume_prompt.continue')) as HTMLButtonElement;
  const exitBtn = el('button', { class: 'ghost', type: 'button' },
    i18n.t('session.resume_prompt.save_and_exit')) as HTMLButtonElement;

  continueBtn.addEventListener('click', () => { dismiss(); onContinue(); });
  exitBtn.addEventListener('click', () => { dismiss(); onSaveAndExit(); });

  root.append(
    el('h3', { class: 'resume-prompt-title' }, i18n.t('session.resume_prompt.title')),
    el('p', { class: 'resume-prompt-body' }, i18n.t('session.resume_prompt.body')),
    el('div', { class: 'resume-prompt-actions' }, [continueBtn, exitBtn]),
  );

  if (autoDismissMs > 0 && typeof window !== 'undefined') {
    window.setTimeout(dismiss, autoDismissMs);
  }
  return root;
}

/**
 * §4.4a v2.3 — DistributionPanel renders an OutcomeDist (trinomial) for one
 * choice × horizon. Shows probability bars for typical / positive_tail /
 * negative_tail with median delta on the most-affected component. Source
 * short-code attached as a base-rate chip (G18).
 *
 * Minimalist: the point is calibration signal, not data viz theatre.
 */
export interface OutcomeDistView {
  typical: { probability: number; median: Record<string, number> };
  positive_tail: { probability: number; median: Record<string, number>; is_exceptional?: boolean };
  negative_tail: { probability: number; median: Record<string, number>; is_catastrophic?: boolean };
  source: string;
}

export function DistributionPanel({
  i18n,
  dist,
}: { i18n: I18n; dist: OutcomeDistView }): HTMLElement {
  const fmt = (n: number): string => `${Math.round(n * 100)}%`;
  const sumHl = (m: Record<string, number>): number => {
    let s = 0;
    for (const v of Object.values(m)) s += Math.abs(v);
    return Math.round(s * 10) / 10;
  };

  const row = (label: string, probability: number, median: Record<string, number>, cls: string) => {
    const pct = Math.round(probability * 100);
    return el('div', { class: `distribution-row ${cls}` }, [
      el('span', { class: 'distribution-label' }, label),
      el('span', { class: 'distribution-bar', style: `--pct:${pct}%` }),
      el('span', { class: 'distribution-prob' }, fmt(probability)),
      el('span', { class: 'distribution-magnitude' }, `Δ≈${sumHl(median)}`),
    ]);
  };

  return el('div', { class: 'distribution-panel', role: 'group', 'aria-label': i18n.t('distribution.heading') }, [
    el('h4', {}, i18n.t('distribution.heading')),
    row(i18n.t('distribution.typical'), dist.typical.probability, dist.typical.median, 'typical'),
    row(i18n.t('distribution.positive_tail'), dist.positive_tail.probability, dist.positive_tail.median, 'positive'),
    row(i18n.t('distribution.negative_tail'), dist.negative_tail.probability, dist.negative_tail.median, 'negative'),
    el('p', { class: 'distribution-source' }, `${i18n.t('distribution.source')}: ${dist.source}`),
  ]);
}

/**
 * §3a Expert Mode — renders a trigger button that opens a modal dialog
 * with the full 21-dimensional profile in a wide table. Modal escapes
 * the narrow card grid so values are readable on desktop.
 * Only called when scenario payload carries `expertParams`.
 */
function expertDetails(
  i18n: I18n,
  params: Record<string, unknown>,
  sources: Record<string, string> = {},
  candidateName: string,
): HTMLElement {
  const rows = Object.entries(params).map(([key, value]) => {
    const src = sources[key];

    let val: string;
    if (Array.isArray(value)) {
      val = value.join(', ');
    } else if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value as Record<string, unknown>);
      val = entries.map(([k, v]) => `${k}: ${v}`).join(', ');
    } else {
      val = String(value);
    }

    const translationKey = `expert_param.${key}`;
    const translatedKey = i18n.t(translationKey);
    const displayKey = translatedKey === translationKey ? key : translatedKey;

    const helpKey = `expert_param.${key}_help`;
    const helpText = i18n.has(helpKey) ? i18n.t(helpKey) : '';

    return el('tr', { class: 'expert-row' }, [
      el('td', { class: 'expert-key' }, displayKey),
      el('td', { class: 'expert-value' }, [
        el('span', { class: 'expert-value-number' }, val),
        helpText ? el('span', { class: 'expert-value-help' }, helpText) : null,
      ]),
      el('td', { class: 'expert-source' }, src ? `[${src}]` : ''),
    ]);
  });

  const closeBtn = el('button', {
    type: 'button',
    class: 'expert-modal-close',
    'aria-label': i18n.t('common.close') || 'Close',
  }, '×') as HTMLButtonElement;

  const dialog = el('dialog', { class: 'expert-modal' }, [
    el('div', { class: 'expert-modal-header' }, [
      el('div', { class: 'expert-modal-titles' }, [
        el('h2', { class: 'expert-modal-title' }, i18n.t('expert.toggle')),
        el('p', { class: 'expert-modal-subtitle' }, candidateName),
      ]),
      closeBtn,
    ]),
    el('div', { class: 'expert-modal-body' }, [
      el('table', { class: 'expert-table' }, [
        el('tbody', {}, rows),
      ]),
    ]),
  ]) as HTMLDialogElement;

  closeBtn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    dialog.close();
  });

  // Close on backdrop click (clicking on the dialog itself, outside content).
  dialog.addEventListener('click', (ev) => {
    if (ev.target === dialog) dialog.close();
  });

  const trigger = el('button', {
    type: 'button',
    class: 'expert-trigger',
  }, i18n.t('expert.toggle')) as HTMLButtonElement;

  trigger.addEventListener('click', (ev) => {
    ev.stopPropagation();
    if (!dialog.isConnected) document.body.appendChild(dialog);
    dialog.showModal();
  });
  trigger.addEventListener('keydown', (ev) => {
    const k = (ev as KeyboardEvent).key;
    if (k === 'Enter' || k === ' ') ev.stopPropagation();
  });

  // Clean up modal from DOM when closed (prevents leak on long sessions).
  dialog.addEventListener('close', () => {
    if (dialog.isConnected) dialog.remove();
  });

  return trigger;
}

function argDetails(i18n: I18n, argKeys: readonly string[]): HTMLElement {
  const node = el('details', { class: 'reasons' }, [
    el('summary', {}, i18n.t('scenario.args.toggle')),
    el('ul', {}, argKeys.map((key) => el('li', {}, i18n.t(key)))),
  ]);
  // Don't let clicks inside the disclosure trigger card selection.
  node.addEventListener('click', (ev) => ev.stopPropagation());
  node.addEventListener('keydown', (ev) => {
    const k = (ev as KeyboardEvent).key;
    if (k === 'Enter' || k === ' ') ev.stopPropagation();
  });
  return node;
}

function infoBlock(
  variant: 'caution' | 'accent' | 'default',
  heading: string,
  body: string,
  extra?: HTMLElement,
): HTMLElement {
  const klass = variant === 'default' ? 'info-block' : `info-block ${variant}`;
  return el('aside', { class: klass, role: 'note' }, [
    el('span', { class: 'info-icon', 'aria-hidden': 'true' }, variant === 'caution' ? '⚠' : 'ⓘ'),
    el('div', { class: 'info-body' }, [
      el('h3', {}, heading),
      el('p', {}, body),
      extra ?? null,
    ]),
  ]);
}

/**
 * SPLIT allocation panel. Renders 2–4 sliders totaling 100% across candidates.
 * Disabled when the scenario declares NO_SPLIT — caller guards before rendering.
 * On submit the consumer receives `{ candidateKey → percent }` that sums to 100 ± 1.
 */
interface SplitPanelProps {
  i18n: I18n;
  candidates: ReadonlyArray<{ candidateKey: string; name: string; labelKey: string | null }>;
  onSubmit: (allocation: Record<string, number>) => void;
  onCancel: () => void;
}

export function SplitPanel({ i18n, candidates, onSubmit, onCancel }: SplitPanelProps): HTMLElement {
  // Start with an even split (rounded so sum=100). The last candidate absorbs
  // any rounding residual so we never show 33/33/33 summing to 99.
  const initial = Math.floor(100 / candidates.length);
  const values = new Map<string, number>();
  candidates.forEach((c, i) => {
    if (i === candidates.length - 1) {
      values.set(c.candidateKey, 100 - initial * (candidates.length - 1));
    } else {
      values.set(c.candidateKey, initial);
    }
  });

  const valueBadges = new Map<string, HTMLElement>();
  const totalBadge = el('span', { class: 'split-total', 'aria-live': 'polite' }, '100%');
  const submitBtn = el('button', { class: 'block', type: 'button' }, i18n.t('scenario.confirm')) as HTMLButtonElement;

  const refreshTotal = () => {
    let total = 0;
    for (const v of values.values()) total += v;
    totalBadge.textContent = `${total}%`;
    const ok = Math.abs(total - 100) <= 1;
    totalBadge.classList.toggle('split-total-ok', ok);
    totalBadge.classList.toggle('split-total-bad', !ok);
    submitBtn.disabled = !ok;
  };

  const rows = candidates.map((c) => {
    const sliderId = `split-${c.candidateKey}`;
    const slider = el('input', {
      type: 'range',
      min: '0',
      max: '100',
      step: '1',
      value: String(values.get(c.candidateKey) ?? 0),
      id: sliderId,
      class: 'split-slider',
      'aria-label': c.name || c.candidateKey,
    }) as HTMLInputElement;
    const badge = el('span', { class: 'split-value' }, `${slider.value}%`);
    valueBadges.set(c.candidateKey, badge);
    slider.addEventListener('input', () => {
      values.set(c.candidateKey, Number.parseInt(slider.value, 10));
      badge.textContent = `${slider.value}%`;
      refreshTotal();
    });
    return el('div', { class: 'split-row' }, [
      el('label', { for: sliderId, class: 'split-label' },
        c.name || (c.labelKey ? i18n.t(c.labelKey) : c.candidateKey)),
      slider,
      badge,
    ]);
  });

  const cancelBtn = el('button', { class: 'ghost', type: 'button' }, i18n.t('scenario.skip.confirm').replace(/^.*$/, i18n.t('reflection.skip'))) as HTMLButtonElement;

  submitBtn.addEventListener('click', () => {
    const out: Record<string, number> = {};
    for (const [k, v] of values.entries()) out[k] = v;
    submitBtn.disabled = true;
    cancelBtn.disabled = true;
    onSubmit(out);
  });
  cancelBtn.addEventListener('click', () => {
    submitBtn.disabled = true;
    cancelBtn.disabled = true;
    onCancel();
  });

  refreshTotal();

  return el('div', { class: 'split-panel', role: 'region', 'aria-label': i18n.t('scenario.split.toggle') }, [
    el('h3', { class: 'split-heading' }, i18n.t('scenario.split.toggle')),
    el('div', { class: 'split-rows' }, rows),
    el('div', { class: 'split-footer' }, [
      el('span', { class: 'split-total-label' }, i18n.t('scenario.budget')),
      totalBadge,
    ]),
    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-primary' }, [submitBtn]),
      el('div', { class: 'actions-secondary' }, [cancelBtn]),
    ]),
  ]);
}

interface ReflectionPanelProps {
  i18n: I18n;
  onContinue: (reflection: Reflection) => void;
  onSkip: () => void;
}

// §9a.3 reflection capture. Rendered between the scenario answer and the
// outcome. Everything is optional: the participant can click "Skip" without
// touching the controls and no reflection fields are sent. The slider does
// not auto-collect when skipped — a 50 default would otherwise look like an
// explicit mid-confidence answer in the dataset.
function ReflectionPanel({ i18n, onContinue, onSkip }: ReflectionPanelProps): HTMLElement {
  const selectedTags = new Set<string>();
  let confidenceTouched = false;

  const slider = el('input', {
    type: 'range',
    min: '0',
    max: '100',
    step: '1',
    value: '50',
    id: 'reflection-confidence',
    class: 'reflection-slider',
    'aria-label': i18n.t('reflection.confidence_label'),
  }) as HTMLInputElement;
  const valueBadge = el('span', { class: 'reflection-value', 'aria-live': 'polite' }, '50');
  const updateBadge = () => {
    valueBadge.textContent = slider.value;
    confidenceTouched = true;
  };
  slider.addEventListener('input', updateBadge);
  slider.addEventListener('change', updateBadge);

  const tagsContainer = el('div', {
    class: 'reflection-tags',
    role: 'group',
    'aria-label': i18n.t('reflection.tags_label'),
  });
  for (const tag of MOTIVATION_TAGS) {
    const id = `reflection-tag-${tag}`;
    const checkbox = el('input', { type: 'checkbox', id, value: tag }) as HTMLInputElement;
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) selectedTags.add(tag);
      else selectedTags.delete(tag);
    });
    const pill = el('label', { class: 'reflection-tag', for: id }, [
      checkbox,
      el('span', {}, i18n.t(`motivation.${tag}`)),
    ]);
    tagsContainer.appendChild(pill);
  }

  const freetext = el('input', {
    type: 'text',
    maxlength: '40',
    class: 'reflection-freetext',
    placeholder: i18n.t('reflection.freetext_placeholder'),
    'aria-label': i18n.t('reflection.freetext_placeholder'),
  }) as HTMLInputElement;

  const continueBtn = el('button', { class: 'block', type: 'button' }, i18n.t('reflection.continue')) as HTMLButtonElement;
  const skipBtn = el('button', { class: 'ghost', type: 'button' }, i18n.t('reflection.skip')) as HTMLButtonElement;

  continueBtn.addEventListener('click', () => {
    const tags = [...selectedTags];
    const extra = freetext.value.trim();
    if (extra.length > 0) tags.push(extra.slice(0, 40));
    const reflection: Reflection = {};
    if (confidenceTouched) reflection.confidence = Number.parseInt(slider.value, 10);
    if (tags.length > 0) reflection.motivationTags = tags;
    continueBtn.disabled = true;
    skipBtn.disabled = true;
    onContinue(reflection);
  });
  skipBtn.addEventListener('click', () => {
    continueBtn.disabled = true;
    skipBtn.disabled = true;
    onSkip();
  });

  return el('div', { class: 'reflection-panel', role: 'region', 'aria-label': i18n.t('reflection.heading') }, [
    el('h3', { class: 'reflection-heading' }, i18n.t('reflection.heading')),
    el('div', { class: 'reflection-confidence' }, [
      el('label', { for: 'reflection-confidence' }, i18n.t('reflection.confidence_label')),
      el('div', { class: 'reflection-slider-row' }, [
        el('span', { class: 'reflection-anchor' }, i18n.t('reflection.confidence_low')),
        slider,
        el('span', { class: 'reflection-anchor' }, i18n.t('reflection.confidence_high')),
      ]),
      valueBadge,
    ]),
    el('div', { class: 'reflection-tags-block' }, [
      el('p', { class: 'reflection-label' }, i18n.t('reflection.tags_label')),
      tagsContainer,
      freetext,
    ]),
    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-primary' }, [continueBtn]),
      el('div', { class: 'actions-secondary' }, [skipBtn]),
    ]),
  ]);
}

export interface ScenarioProps {
  i18n: I18n;
  view: ScenarioView;
  onSubmitted: (ack: ResponseAck) => void;
  onError: (err: unknown) => void;
}

export function ScenarioScreen({ i18n, view, onSubmitted, onError }: ScenarioProps): HTMLElement {
  const flags = view.scenario.flags ?? [];
  const allowOptOut = !flags.includes('NO_OPT_OUT');
  const allowSplit = !flags.includes('NO_SPLIT') && view.scenario.decisionType === 'SPLIT';
  const startedAt = performance.now();

  let chosen: string | null = null;
  const promptId = `scenario-prompt-${view.index}`;
  const section = el('section', { 'aria-label': 'Scenario' });

  // Sticky top header: progress + count.
  const total = view.total;
  const current = view.index + 1;
  const progressPct = Math.round((view.index / total) * 100);
  const progressBar = el('div', {
    class: 'progress',
    role: 'progressbar',
    'aria-valuemin': '0',
    'aria-valuemax': String(total),
    'aria-valuenow': String(view.index),
    'aria-label': i18n.t('scenario.header.title', { current, total }),
  }, [
    el('span', { style: `inline-size:${progressPct}%` }),
  ]);
  const header = el('div', { class: 'scenario-header' }, [
    el('div', { class: 'scenario-header-row' }, [
      el('span', { class: 'scenario-count' }, [
        el('strong', {}, String(current)),
        ' / ' + String(total),
      ]),
      progressBar,
    ]),
  ]);
  section.appendChild(header);

  // Calibration note — as an info-block.
  if (view.isCalibration) {
    section.appendChild(
      infoBlock('default', i18n.t('scenario.flags.neutrality') || 'Note', i18n.t('scenario.calibration_note')),
    );
  }

  // Twist — caution info-block (semantic emphasis).
  if (view.twist) {
    const twistBody =
      view.twist.type === 'TARGETING_ERROR'
        ? i18n.t('twist.targeting_error')
        : i18n.t('twist.shock');
    section.appendChild(infoBlock('caution', i18n.t('twist.heading'), twistBody));
  }

  // Prompt + flags.
  section.appendChild(el('h1', { class: 'scenario-prompt', id: promptId }, i18n.t(view.scenario.promptKey)));

  if (flags.length > 0) {
    const flagPills = el('div', { class: 'flags', 'aria-label': 'Scenario constraints' });
    for (const f of flags) {
      const key =
        f === 'NO_SPLIT' ? 'scenario.flags.no_split' :
        f === 'NO_OPT_OUT' ? 'scenario.flags.no_opt_out' :
        f === 'NEUTRALITY_TEST' ? 'scenario.flags.neutrality' :
        f === 'RETURNING_BENEFICIARY' ? 'scenario.flags.returning' :
        null;
      if (key) {
        const helpKey = `${key}_help`;
        const hasHelp = i18n.has(helpKey);
        const emphasis = f === 'NO_OPT_OUT' || f === 'NO_SPLIT' ? 'flag-pill strong' : 'flag-pill';
        const attrs: Record<string, string> = { class: emphasis };
        if (hasHelp) {
          const helpText = i18n.t(helpKey);
          attrs.title = helpText;
          attrs['aria-label'] = `${i18n.t(key)}: ${helpText}`;
        }
        flagPills.appendChild(
          el(
            'span',
            attrs,
            hasHelp ? [i18n.t(key), el('span', { class: 'flag-info', 'aria-hidden': 'true' }, 'ⓘ')] : i18n.t(key),
          ),
        );
      }
    }
    section.appendChild(flagPills);
  }

  // Budget — promoted out of the "cost" label.
  section.appendChild(el('div', { class: 'budget', role: 'group', 'aria-label': i18n.t('scenario.budget') }, [
    el('span', { class: 'budget-label' }, i18n.t('scenario.budget')),
    el('span', { class: 'budget-value' }, String(view.scenario.budget)),
    el('span', { class: 'budget-unit' }, i18n.t('scenario.cost_unit')),
  ]));

  // Group contrarian args by choice.
  const argsByChoice = new Map<string, string[]>();
  for (const arg of view.scenario.contrarianArgs ?? []) {
    const list = argsByChoice.get(arg.choice) ?? [];
    list.push(arg.argument_key);
    argsByChoice.set(arg.choice, list);
  }

  const candidates = view.scenario.candidates;
  const colClass =
    candidates.length >= 4 ? 'candidates cols-3' :
    candidates.length >= 2 ? 'candidates cols-2' :
    'candidates';
  const candidatesWrap = el('div', {
    class: colClass,
    role: 'radiogroup',
    'aria-labelledby': promptId,
  });

  const cards: HTMLElement[] = [];

  const select = (index: number) => {
    chosen = candidates[index]!.choiceKey;
    cards.forEach((c, i) => {
      const selected = i === index;
      c.classList.toggle('selected', selected);
      c.setAttribute('aria-checked', selected ? 'true' : 'false');
      c.setAttribute('tabindex', selected ? '0' : '-1');
    });
    submit.disabled = false;
  };

  // §2 narrative emphasis — role's bias_matrix decides which card elements
  // receive visual emphasis. Data values are never changed; we only add a CSS
  // class so the renderer can lift typography/contrast on the highlighted slot.
  const emph = new Set<EmphasisTag>(view.narrativeEmphasis ?? []);
  const klass = (base: string, tag: EmphasisTag) =>
    emph.has(tag) ? `${base} emphasised` : base;

  candidates.forEach((c, i) => {
    const cardId = `choice-${c.choiceKey}`;
    const cardArgs = argsByChoice.get(c.choiceKey) ?? [];
    const card = el('div', {
      class: 'card',
      role: 'radio',
      tabindex: i === 0 ? '0' : '-1',
      'aria-checked': 'false',
      id: cardId,
    }, [
      el('h3', { class: klass('', 'nameHighlight').trim() || undefined },
        c.name || (c.labelKey ? i18n.t(c.labelKey) : c.archetypeId)),
      c.labelKey ? el('p', { class: 'card-sub' }, i18n.t(c.labelKey)) : null,
      c.storyKey ? el('p', { class: klass('card-story', 'storyKey') }, i18n.t(c.storyKey)) : null,
      c.conflictKey ? el('p', { class: klass('card-sub', 'conflictKey') }, i18n.t(c.conflictKey)) : null,
      c.capabilities && c.capabilities.length > 0
        ? el('ul', { class: klass('card-capabilities', 'capabilities') },
            c.capabilities.map((cap) => el('li', {}, i18n.t(`capability.${cap}`))))
        : null,
      el('span', { class: klass('cost-chip', 'packageCost') },
        `${i18n.t('scenario.cost')} · ${c.packageCost} ${i18n.t('scenario.cost_unit')}`),
      cardArgs.length > 0 ? argDetails(i18n, cardArgs) : null,
      c.expertParams && Object.keys(c.expertParams).length > 0
        ? expertDetails(
            i18n,
            c.expertParams,
            c.expertSources ?? {},
            c.name || (c.labelKey ? i18n.t(c.labelKey) : c.archetypeId),
          )
        : null,
    ]);
    card.addEventListener('click', (ev) => {
      // Don't select when clicking inside the disclosure.
      if ((ev.target as HTMLElement).closest('details.reasons')) return;
      select(i);
    });
    card.addEventListener('keydown', (ev) => {
      const k = (ev as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') { ev.preventDefault(); select(i); return; }
      if (k === 'ArrowDown' || k === 'ArrowRight') {
        ev.preventDefault();
        const next = (i + 1) % candidates.length;
        cards[next]!.focus();
        return;
      }
      if (k === 'ArrowUp' || k === 'ArrowLeft') {
        ev.preventDefault();
        const prev = (i - 1 + candidates.length) % candidates.length;
        cards[prev]!.focus();
        return;
      }
    });
    cards.push(card);
    candidatesWrap.appendChild(card);
  });
  section.appendChild(candidatesWrap);

  // IMC attention check (pre-actions so the bottom bar stays uncluttered).
  if (view.showImc) {
    const imc = el('aside', { class: 'info-block accent', role: 'note' });
    const imcAnswer = async (answer: 'continue' | 'skip' | 'random') => {
      try { await api.submitImc(answer, view.index); } catch { /* best effort */ }
      imc.remove();
    };
    imc.appendChild(el('span', { class: 'info-icon', 'aria-hidden': 'true' }, 'ⓘ'));
    imc.appendChild(el('div', { class: 'info-body' }, [
      el('h3', {}, 'Attention check'),
      el('p', {}, i18n.t('imc.prompt')),
      el('div', { class: 'cluster-sm', style: 'margin-block-start: 0.75rem' }, [
        el('button', { class: 'secondary', onClick: () => void imcAnswer('continue') }, i18n.t('imc.options.continue')),
        el('button', { class: 'secondary', onClick: () => void imcAnswer('skip') }, i18n.t('imc.options.skip')),
        el('button', { class: 'secondary', onClick: () => void imcAnswer('random') }, i18n.t('imc.options.random')),
      ]),
    ]));
    section.appendChild(imc);
  }

  // Actions — sticky on mobile, inline on desktop.
  const actions = el('div', { class: 'actions sticky-bottom' });
  const submit = el('button', { class: 'block', disabled: true }, i18n.t('scenario.confirm')) as HTMLButtonElement;
  const refuse = el(
    'button',
    { class: 'ghost', disabled: !allowOptOut },
    i18n.t('scenario.opt_out'),
  ) as HTMLButtonElement;
  const splitBtn = allowSplit
    ? el('button', { class: 'ghost' }, i18n.t('scenario.split.toggle')) as HTMLButtonElement
    : null;
  // §24.4 «I can't decide» — always available, styled as `ghost` so it sits
  // at the same visual weight as refuse/split and never outshines the real
  // choices. Skip is a separate data channel, not a refusal.
  const skipBtn = el(
    'button',
    { class: 'ghost', type: 'button' },
    i18n.t('scenario.skip.button'),
  ) as HTMLButtonElement;

  // Core submit — fires after reflection (or immediately for SKIP which has
  // its own capture path and for which reflection is meaningless).
  const finalise = async (
    choice: string,
    opts: {
      skipReason?: SkipReason;
      reflection?: Reflection;
      splitAllocation?: Record<string, number>;
    } = {},
  ) => {
    submit.disabled = true;
    refuse.disabled = true;
    if (splitBtn) splitBtn.disabled = true;
    skipBtn.disabled = true;
    const elapsed = Math.round(performance.now() - startedAt);
    try {
      const ack = await api.submit(
        view.index,
        choice,
        elapsed,
        opts.skipReason,
        opts.reflection,
        opts.splitAllocation,
      );
      onSubmitted(ack);
    } catch (err) {
      onError(err);
    }
  };

  // §4.3 SPLIT — renders the slider panel in place of the action bar. Once the
  // user confirms a valid allocation we run it through the reflection step,
  // same as a regular choice, and submit the result with splitAllocation.
  let splitAllocation: Record<string, number> | undefined;
  const openSplitPanel = () => {
    const panel = SplitPanel({
      i18n,
      candidates: candidates.map((c) => ({
        candidateKey: c.candidateKey,
        name: c.name,
        labelKey: c.labelKey,
      })),
      onSubmit: (allocation) => {
        splitAllocation = allocation;
        openReflection('split');
      },
      onCancel: () => {
        // Restore the actions bar if the user bails out of the split panel.
        splitAllocation = undefined;
        const restored = actions;
        panel.replaceWith(restored);
        submit.disabled = !chosen;
        refuse.disabled = !allowOptOut;
        if (splitBtn) splitBtn.disabled = false;
        skipBtn.disabled = false;
      },
    });
    actions.replaceWith(panel);
  };

  // §9a.3 reflection step — shown between the primary submit and the outcome.
  // For SKIP we bypass it: the skip-reason panel already captures the data
  // point we need, and asking someone who couldn't decide how confident they
  // were would be incoherent.
  const openReflection = (choice: string) => {
    submit.disabled = true;
    refuse.disabled = true;
    if (splitBtn) splitBtn.disabled = true;
    skipBtn.disabled = true;
    const carriedSplit = choice === 'split' ? splitAllocation : undefined;
    const panel = ReflectionPanel({
      i18n,
      onContinue: (reflection) =>
        void finalise(choice, carriedSplit ? { reflection, splitAllocation: carriedSplit } : { reflection }),
      onSkip: () =>
        void finalise(choice, carriedSplit ? { splitAllocation: carriedSplit } : {}),
    });
    // Replace the actions bar (or split panel) with the reflection panel in
    // place, so the user sees a clear single next step without the scenario
    // cards fighting for attention.
    const current = actions.isConnected ? actions : section.querySelector('.split-panel') as HTMLElement | null;
    (current ?? actions).replaceWith(panel);
  };

  submit.addEventListener('click', () => {
    if (!chosen) return;
    openReflection(chosen);
  });
  refuse.addEventListener('click', () => openReflection('refuse_all'));
  if (splitBtn) splitBtn.addEventListener('click', () => openSplitPanel());

  // Reason picker — hidden until the skip button is activated. Single-select
  // dropdown over the four reasons the task scopes; the fuller §24.4 multi-
  // select UI with exit/softer branches is future work.
  const skipPanel = el('div', { class: 'skip-panel', hidden: true }) as HTMLDivElement;
  const reasonSelect = el('select', {
    'aria-label': i18n.t('scenario.skip.heading'),
  }) as HTMLSelectElement;
  reasonSelect.appendChild(el('option', { value: '' }, i18n.t('survey.choose')));
  for (const r of SKIP_REASONS) {
    reasonSelect.appendChild(el('option', { value: r }, i18n.t(`scenario.skip.reason.${r}`)));
  }
  const skipConfirm = el(
    'button',
    { class: 'ghost', type: 'button', disabled: true },
    i18n.t('scenario.skip.confirm'),
  ) as HTMLButtonElement;
  reasonSelect.addEventListener('change', () => {
    skipConfirm.disabled = !SKIP_REASONS.includes(reasonSelect.value as SkipReason);
  });
  skipBtn.addEventListener('click', () => {
    skipPanel.hidden = !skipPanel.hidden;
    skipBtn.setAttribute('aria-expanded', skipPanel.hidden ? 'false' : 'true');
    if (!skipPanel.hidden) reasonSelect.focus();
  });
  skipBtn.setAttribute('aria-expanded', 'false');
  skipConfirm.addEventListener('click', () => {
    const reason = reasonSelect.value as SkipReason;
    if (!SKIP_REASONS.includes(reason)) return;
    void finalise('SKIP', { skipReason: reason });
  });
  skipPanel.appendChild(el('p', { class: 'skip-heading' }, i18n.t('scenario.skip.heading')));
  skipPanel.appendChild(reasonSelect);
  skipPanel.appendChild(skipConfirm);

  actions.appendChild(el('div', { class: 'actions-primary' }, [submit]));
  const row = el('div', { class: 'actions-secondary' });
  if (allowOptOut) row.appendChild(refuse);
  if (splitBtn) row.appendChild(splitBtn);
  row.appendChild(skipBtn);
  actions.appendChild(row);
  section.appendChild(actions);
  section.appendChild(skipPanel);

  return section;
}

export function OutcomeScreen({
  i18n,
  ack,
  onContinue,
}: { i18n: I18n; ack: ResponseAck; onContinue: () => void }): HTMLElement {
  // Prefer a specific outcome line when the scenario provides one.
  const specific = i18n.t(ack.outcomeChoiceKey);
  const text = specific === ack.outcomeChoiceKey && ack.outcomeFallbackKey
    ? i18n.t(ack.outcomeFallbackKey)
    : specific;
  return el('section', { 'aria-label': 'Outcome', class: 'stack' }, [
    el('div', { class: 'outcome-card' }, [
      el('p', { class: 'outcome-eyebrow' }, i18n.t('outcome.T1.heading')),
      el('p', { class: 'outcome-text' }, text),
    ]),
    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-primary' }, [
        el('button', { class: 'block', onClick: () => onContinue() }, i18n.t('outcome.continue')),
      ]),
    ]),
  ]);
}
